import { HAIR_COLORS, OUTFIT_COLORS, SKIN_TONES, STORAGE_KEY } from "../game/constants";
import { insertScore } from "../game/rules";
import type {
  AccessibilitySettings,
  CharacterConfig,
  DifficultyId,
  Language,
  SaveData,
  ScoreEntry,
  VolumeSettings,
} from "../game/types";

const defaultCharacter = (): CharacterConfig => ({
  id: "boy",
  nickname: "Max",
  skin: SKIN_TONES[1] ?? "#e8b98a",
  hair: HAIR_COLORS[1] ?? "#5a3218",
  outfit: OUTFIT_COLORS[0] ?? "#ff6b8a",
});

export const defaultAccessibility = (): AccessibilitySettings => ({
  screenShake: true,
  textScale: 1,
  reduceMotion: false,
  highContrast: false,
  colorAssist: false,
  aimLengthScale: 1,
});

export const defaultVolumes = (): VolumeSettings => ({
  music: 0.55,
  ambient: 0.4,
  sfx: 0.7,
});

export const defaultSave = (): SaveData => ({
  language: "fr",
  character: defaultCharacter(),
  volumes: defaultVolumes(),
  accessibility: defaultAccessibility(),
  tutorialSeen: false,
  difficulty: "normal",
  scores: { easy: [], normal: [], hard: [] },
});

export class SaveSystem {
  data: SaveData;

  constructor(private readonly storage: Storage) {
    this.data = this.read();
  }

  private read(): SaveData {
    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (!raw) return defaultSave();
      const parsed = JSON.parse(raw) as Partial<SaveData>;
      const base = defaultSave();
      return {
        ...base,
        ...parsed,
        character: { ...base.character, ...parsed.character },
        volumes: { ...base.volumes, ...parsed.volumes },
        accessibility: { ...base.accessibility, ...parsed.accessibility },
        scores: {
          easy: parsed.scores?.easy ?? [],
          normal: parsed.scores?.normal ?? [],
          hard: parsed.scores?.hard ?? [],
        },
      };
    } catch {
      return defaultSave();
    }
  }

  persist(): void {
    this.storage.setItem(STORAGE_KEY, JSON.stringify(this.data));
  }

  setLanguage(language: Language): void {
    this.data.language = language;
    this.persist();
  }

  setCharacter(character: CharacterConfig): void {
    this.data.character = { ...character, nickname: character.nickname.trim() || "Max" };
    this.persist();
  }

  setDifficulty(difficulty: DifficultyId): void {
    this.data.difficulty = difficulty;
    this.persist();
  }

  setVolumes(volumes: VolumeSettings): void {
    this.data.volumes = volumes;
    this.persist();
  }

  setAccessibility(accessibility: AccessibilitySettings): void {
    this.data.accessibility = accessibility;
    this.persist();
  }

  markTutorialSeen(): void {
    this.data.tutorialSeen = true;
    this.persist();
  }

  addScore(difficulty: DifficultyId, entry: ScoreEntry): ScoreEntry[] {
    this.data.scores[difficulty] = insertScore(this.data.scores[difficulty], entry);
    this.persist();
    return this.data.scores[difficulty];
  }

  clearScores(): void {
    this.data.scores = { easy: [], normal: [], hard: [] };
    this.persist();
  }

  resetAll(): void {
    this.data = defaultSave();
    this.persist();
  }
}
